import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useOrgData } from '../../contexts/OrgDataContext';
import { useLocale } from '../../contexts/LocaleContext';
import { DivisionKey, OrgFunction, Division } from '../../types/orgchart.types';
import './InlineEditPopover.css';

export type EditType = 'division' | 'department' | 'section' | 'function' | 'executive' | 'new-department' | 'new-section' | 'new-function';

// Inline Division Edit Component - renders inside the division cell
interface DivisionInlineEditProps {
  divisionKey: DivisionKey;
  division: Division;
  onClose: () => void;
}

export const DivisionInlineEdit: React.FC<DivisionInlineEditProps> = ({ divisionKey, division, onClose }) => {
  const { updateDivision, orgData, getEmployeeById, addEmployee } = useOrgData();
  const { t } = useLocale();

  const [name, setName] = useState(division.name);
  const [vfp, setVfp] = useState(division.vfp || '');
  const [managerId, setManagerId] = useState(division.managerId || '');
  const [color, setColor] = useState(division.color || '#667eea');
  const [icon, setIcon] = useState(division.icon || '📁');
  const [showNewEmployeeForm, setShowNewEmployeeForm] = useState(false);
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [newEmployeeCache, setNewEmployeeCache] = useState<Record<string, string>>({});

  const formRef = useRef<HTMLDivElement>(null);

  // Get all employees for manager dropdown
  const allEmployees = orgData.employees.filter(e =>
    e.id !== 'owner' && e.id !== '0' && e.id !== 'board'
  );

  const handleSave = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    updateDivision(divisionKey, {
      name,
      vfp,
      managerId: managerId || undefined,
      color: color || undefined,
      icon: icon || undefined
    });
    onClose();
  }, [divisionKey, name, vfp, managerId, color, icon, updateDivision, onClose]);

  const handleCancel = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    onClose();
  }, [onClose]);

  const handleCreateNewEmployee = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!newEmployeeName.trim()) return;

    const ceo = orgData.employees.find(emp => emp.id === 'ceo' || emp.id === '1');
    const parentId = ceo?.id || '1';
    const employeeName = newEmployeeName.trim();
    const newEmployee = addEmployee({
      name: employeeName,
      division: divisionKey,
      parentId
    });

    setNewEmployeeCache(prev => ({ ...prev, [newEmployee.id]: employeeName }));
    setManagerId(newEmployee.id);
    setShowNewEmployeeForm(false);
    setNewEmployeeName('');
  }, [newEmployeeName, orgData.employees, addEmployee, divisionKey]);

  // Prevent click propagation to parent division cell
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const managerName = managerId ? (getEmployeeById(managerId)?.name || newEmployeeCache[managerId]) : null;

  return (
    <div className="division-inline-edit" onClick={handleContainerClick} ref={formRef} style={{ '--edit-color': color } as React.CSSProperties}>
      {/* Header with division number, icon picker, and color */}
      <div className="inline-edit-header" style={{ backgroundColor: color }}>
        <div className="inline-header-left">
          <span className="inline-division-num">{division.number}</span>
          <input
            type="text"
            value={icon}
            onChange={e => setIcon(e.target.value)}
            className="inline-icon-input"
            maxLength={2}
            onClick={e => e.stopPropagation()}
            title={t('edit.icon')}
          />
        </div>
        <div className="inline-header-right">
          <input
            type="color"
            value={color}
            onChange={e => setColor(e.target.value)}
            className="inline-header-color"
            onClick={e => e.stopPropagation()}
            title={t('edit.color')}
          />
          <button className="inline-close-btn" onClick={handleCancel} title={t('general.cancel')}>×</button>
        </div>
      </div>

      {/* Main content */}
      <div className="inline-edit-body">
        {/* Name field - prominent */}
        <div className="inline-field-group">
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('edit.name')}
            className="inline-name-input"
            autoFocus
            onClick={e => e.stopPropagation()}
          />
        </div>

        {/* Manager field - compact inline */}
        <div className="inline-field-group inline-manager-group">
          <span className="inline-field-icon">👤</span>
          {managerId ? (
            <div className="inline-manager-display">
              <span>{managerName || t('edit.notFound')}</span>
              <button onClick={(e) => { e.stopPropagation(); setManagerId(''); }} className="inline-clear-btn">×</button>
            </div>
          ) : (
            showNewEmployeeForm ? (
              <div className="inline-new-manager-form">
                <input
                  type="text"
                  value={newEmployeeName}
                  onChange={e => setNewEmployeeName(e.target.value)}
                  placeholder={t('edit.managerName')}
                  onClick={e => e.stopPropagation()}
                  className="inline-new-manager-input"
                />
                <button onClick={handleCreateNewEmployee} disabled={!newEmployeeName.trim()} className="inline-confirm-btn">✓</button>
                <button onClick={(e) => { e.stopPropagation(); setShowNewEmployeeForm(false); setNewEmployeeName(''); }} className="inline-cancel-small">×</button>
              </div>
            ) : (
              <div className="inline-manager-picker">
                <select
                  value=""
                  onChange={e => { if (e.target.value) setManagerId(e.target.value); }}
                  onClick={e => e.stopPropagation()}
                  className="inline-manager-dropdown"
                >
                  <option value="">{t('edit.selectManager')}</option>
                  {allEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
                <button onClick={(e) => { e.stopPropagation(); setShowNewEmployeeForm(true); }} className="inline-add-btn" title={t('edit.newEmployee')}>+</button>
              </div>
            )
          )}
        </div>

        {/* VFP field */}
        <div className="inline-field-group inline-vfp-group">
          <label className="inline-vfp-label">{t('edit.vfp')}</label>
          <textarea
            value={vfp}
            onChange={e => setVfp(e.target.value)}
            placeholder={t('edit.finalProduct')}
            rows={2}
            className="inline-vfp-input"
            onClick={e => e.stopPropagation()}
          />
        </div>
      </div>

      {/* Footer with save button */}
      <div className="inline-edit-footer">
        <button className="btn-inline-save" onClick={handleSave}>
          {t('general.save')}
        </button>
      </div>
    </div>
  );
};

// Inline Department Edit Component - renders inside the department cell
interface DepartmentInlineEditProps {
  divisionKey: DivisionKey;
  deptIndex: number;
  divisionColor: string;
  onClose: () => void;
}

export const DepartmentInlineEdit: React.FC<DepartmentInlineEditProps> = ({ divisionKey, deptIndex, divisionColor, onClose }) => {
  const { orgData, updateDepartment, getEmployeeById, addEmployee } = useOrgData();
  const { t } = useLocale();

  const division = orgData.divisions[divisionKey];
  const dept = division?.departments?.[deptIndex];

  const [name, setName] = useState(dept?.name || '');
  const [vfp, setVfp] = useState(dept?.vfp || '');
  const [managerId, setManagerId] = useState(dept?.managerId || '');
  const [showNewEmployeeForm, setShowNewEmployeeForm] = useState(false);
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [newEmployeeCache, setNewEmployeeCache] = useState<Record<string, string>>({});

  const formRef = useRef<HTMLDivElement>(null);

  // Get all employees for manager dropdown
  const allEmployees = orgData.employees.filter(e =>
    e.id !== 'owner' && e.id !== '0' && e.id !== 'board'
  );

  const handleSave = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    updateDepartment(divisionKey, deptIndex, {
      name,
      vfp,
      managerId: managerId || undefined
    });
    onClose();
  }, [divisionKey, deptIndex, name, vfp, managerId, updateDepartment, onClose]);

  const handleCancel = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    onClose();
  }, [onClose]);

  const handleCreateNewEmployee = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!newEmployeeName.trim()) return;

    const ceo = orgData.employees.find(emp => emp.id === 'ceo' || emp.id === '1');
    const parentId = ceo?.id || '1';
    const employeeName = newEmployeeName.trim();
    const newEmployee = addEmployee({
      name: employeeName,
      division: divisionKey,
      deptNum: dept?.number,
      parentId
    });

    setNewEmployeeCache(prev => ({ ...prev, [newEmployee.id]: employeeName }));
    setManagerId(newEmployee.id);
    setShowNewEmployeeForm(false);
    setNewEmployeeName('');
  }, [newEmployeeName, orgData.employees, addEmployee, divisionKey, dept?.number]);

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const managerName = managerId ? (getEmployeeById(managerId)?.name || newEmployeeCache[managerId]) : null;

  if (!dept) return null;

  return (
    <div className="department-inline-edit" onClick={handleContainerClick} ref={formRef} style={{ '--edit-color': divisionColor } as React.CSSProperties}>
      {/* Header */}
      <div className="inline-edit-header" style={{ backgroundColor: divisionColor }}>
        <div className="inline-header-left">
          <span className="inline-division-num">{t('board.department')} {dept.number}</span>
        </div>
        <div className="inline-header-right">
          <button className="inline-close-btn" onClick={handleCancel} title={t('general.cancel')}>×</button>
        </div>
      </div>

      {/* Body */}
      <div className="inline-edit-body">
        {/* Name field */}
        <div className="inline-field-group">
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('edit.name')}
            className="inline-name-input"
            autoFocus
            onClick={e => e.stopPropagation()}
          />
        </div>

        {/* Manager field */}
        <div className="inline-field-group inline-manager-group">
          <span className="inline-field-icon">👤</span>
          {managerId ? (
            <div className="inline-manager-display">
              <span>{managerName || t('edit.notFound')}</span>
              <button onClick={(e) => { e.stopPropagation(); setManagerId(''); }} className="inline-clear-btn">×</button>
            </div>
          ) : (
            showNewEmployeeForm ? (
              <div className="inline-new-manager-form">
                <input
                  type="text"
                  value={newEmployeeName}
                  onChange={e => setNewEmployeeName(e.target.value)}
                  placeholder={t('edit.managerName')}
                  onClick={e => e.stopPropagation()}
                  className="inline-new-manager-input"
                />
                <button onClick={handleCreateNewEmployee} disabled={!newEmployeeName.trim()} className="inline-confirm-btn">✓</button>
                <button onClick={(e) => { e.stopPropagation(); setShowNewEmployeeForm(false); setNewEmployeeName(''); }} className="inline-cancel-small">×</button>
              </div>
            ) : (
              <div className="inline-manager-picker">
                <select
                  value=""
                  onChange={e => { if (e.target.value) setManagerId(e.target.value); }}
                  onClick={e => e.stopPropagation()}
                  className="inline-manager-dropdown"
                >
                  <option value="">{t('edit.selectManager')}</option>
                  {allEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
                <button onClick={(e) => { e.stopPropagation(); setShowNewEmployeeForm(true); }} className="inline-add-btn" title={t('edit.newEmployee')}>+</button>
              </div>
            )
          )}
        </div>

        {/* VFP field */}
        <div className="inline-field-group inline-vfp-group">
          <label className="inline-vfp-label">{t('edit.vfp')}</label>
          <textarea
            value={vfp}
            onChange={e => setVfp(e.target.value)}
            placeholder={t('edit.finalProduct')}
            rows={2}
            className="inline-vfp-input"
            onClick={e => e.stopPropagation()}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="inline-edit-footer">
        <button className="btn-inline-save" onClick={handleSave}>
          {t('general.save')}
        </button>
      </div>
    </div>
  );
};

// Inline Section Edit Component - renders inside the section
interface SectionInlineEditProps {
  divisionKey: DivisionKey;
  deptIndex: number;
  sectionIndex: number;
  divisionColor: string;
  onClose: () => void;
}

export const SectionInlineEdit: React.FC<SectionInlineEditProps> = ({ divisionKey, deptIndex, sectionIndex, divisionColor, onClose }) => {
  const { orgData, updateSection, getEmployeeById, addEmployee } = useOrgData();
  const { t } = useLocale();

  const division = orgData.divisions[divisionKey];
  const dept = division?.departments?.[deptIndex];
  const section = dept?.sections?.[sectionIndex];

  const [name, setName] = useState(section?.name || '');
  const [managerId, setManagerId] = useState(section?.managerId || '');
  const [showNewEmployeeForm, setShowNewEmployeeForm] = useState(false);
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [newEmployeeCache, setNewEmployeeCache] = useState<Record<string, string>>({});

  const formRef = useRef<HTMLDivElement>(null);

  const allEmployees = orgData.employees.filter(e =>
    e.id !== 'owner' && e.id !== '0' && e.id !== 'board'
  );

  const handleSave = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    updateSection(divisionKey, deptIndex, sectionIndex, {
      name,
      managerId: managerId || undefined
    });
    onClose();
  }, [divisionKey, deptIndex, sectionIndex, name, managerId, updateSection, onClose]);

  const handleCancel = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    onClose();
  }, [onClose]);

  const handleCreateNewEmployee = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!newEmployeeName.trim()) return;

    const ceo = orgData.employees.find(emp => emp.id === 'ceo' || emp.id === '1');
    const parentId = ceo?.id || '1';
    const employeeName = newEmployeeName.trim();
    const newEmployee = addEmployee({
      name: employeeName,
      division: divisionKey,
      deptNum: dept?.number,
      parentId
    });

    setNewEmployeeCache(prev => ({ ...prev, [newEmployee.id]: employeeName }));
    setManagerId(newEmployee.id);
    setShowNewEmployeeForm(false);
    setNewEmployeeName('');
  }, [newEmployeeName, orgData.employees, addEmployee, divisionKey, dept?.number]);

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const managerName = managerId ? (getEmployeeById(managerId)?.name || newEmployeeCache[managerId]) : null;

  if (!section) return null;

  return (
    <div className="section-inline-edit" onClick={handleContainerClick} ref={formRef} style={{ '--edit-color': divisionColor } as React.CSSProperties}>
      {/* Header */}
      <div className="inline-edit-header section-edit-header" style={{ backgroundColor: divisionColor }}>
        <div className="inline-header-left">
          <span className="inline-section-label">{t('edit.editSection')}</span>
        </div>
        <div className="inline-header-right">
          <button className="inline-close-btn" onClick={handleCancel} title={t('general.cancel')}>×</button>
        </div>
      </div>

      {/* Body */}
      <div className="inline-edit-body">
        {/* Name field */}
        <div className="inline-field-group">
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('edit.name')}
            className="inline-name-input"
            autoFocus
            onClick={e => e.stopPropagation()}
          />
        </div>

        {/* Manager field */}
        <div className="inline-field-group inline-manager-group">
          <span className="inline-field-icon">👤</span>
          {managerId ? (
            <div className="inline-manager-display">
              <span>{managerName || t('edit.notFound')}</span>
              <button onClick={(e) => { e.stopPropagation(); setManagerId(''); }} className="inline-clear-btn">×</button>
            </div>
          ) : (
            showNewEmployeeForm ? (
              <div className="inline-new-manager-form">
                <input
                  type="text"
                  value={newEmployeeName}
                  onChange={e => setNewEmployeeName(e.target.value)}
                  placeholder={t('edit.managerName')}
                  onClick={e => e.stopPropagation()}
                  className="inline-new-manager-input"
                />
                <button onClick={handleCreateNewEmployee} disabled={!newEmployeeName.trim()} className="inline-confirm-btn">✓</button>
                <button onClick={(e) => { e.stopPropagation(); setShowNewEmployeeForm(false); setNewEmployeeName(''); }} className="inline-cancel-small">×</button>
              </div>
            ) : (
              <div className="inline-manager-picker">
                <select
                  value=""
                  onChange={e => { if (e.target.value) setManagerId(e.target.value); }}
                  onClick={e => e.stopPropagation()}
                  className="inline-manager-dropdown"
                >
                  <option value="">{t('edit.selectManager')}</option>
                  {allEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
                <button onClick={(e) => { e.stopPropagation(); setShowNewEmployeeForm(true); }} className="inline-add-btn" title={t('edit.newEmployee')}>+</button>
              </div>
            )
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="inline-edit-footer">
        <button className="btn-inline-save" onClick={handleSave}>
          {t('general.save')}
        </button>
      </div>
    </div>
  );
};

// Inline Post/Function Edit Component - renders inside the function item
interface PostInlineEditProps {
  divisionKey: DivisionKey;
  deptIndex: number;
  sectionIndex: number;
  funcIndex: number;
  divisionColor: string;
  onClose: () => void;
}

export const PostInlineEdit: React.FC<PostInlineEditProps> = ({ divisionKey, deptIndex, sectionIndex, funcIndex, divisionColor, onClose }) => {
  const { orgData, updateFunction, updateAssignments, getEmployeeById, addEmployee } = useOrgData();
  const { t } = useLocale();

  const division = orgData.divisions[divisionKey];
  const dept = division?.departments?.[deptIndex];
  const section = dept?.sections?.[sectionIndex];
  const func = section?.functions?.[funcIndex];
  const funcId = func?.id || `${divisionKey}-d${deptIndex}-s${sectionIndex}-f${funcIndex}`;

  const [name, setName] = useState(func?.name || '');
  const [objectives, setObjectives] = useState<string[]>(func?.objectives || []);
  const [assignedEmployees, setAssignedEmployees] = useState<string[]>(orgData.assignments?.[funcId] || []);
  const [showNewEmployeeForm, setShowNewEmployeeForm] = useState(false);
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [newEmployeeCache, setNewEmployeeCache] = useState<Record<string, string>>({});

  const formRef = useRef<HTMLDivElement>(null);

  const allEmployees = orgData.employees.filter(e =>
    e.id !== 'owner' && e.id !== '0' && e.id !== 'board'
  );

  const handleSave = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    updateFunction(divisionKey, deptIndex, sectionIndex, funcIndex, {
      name,
      objectives: objectives.filter(o => o.trim())
    });
    updateAssignments(funcId, assignedEmployees);
    onClose();
  }, [divisionKey, deptIndex, sectionIndex, funcIndex, name, objectives, funcId, assignedEmployees, updateFunction, updateAssignments, onClose]);

  const handleCancel = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    onClose();
  }, [onClose]);

  const handleCreateNewEmployee = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!newEmployeeName.trim()) return;

    const ceo = orgData.employees.find(emp => emp.id === 'ceo' || emp.id === '1');
    const parentId = ceo?.id || '1';
    const employeeName = newEmployeeName.trim();
    const newEmployee = addEmployee({
      name: employeeName,
      division: divisionKey,
      deptNum: dept?.number,
      parentId
    });

    setNewEmployeeCache(prev => ({ ...prev, [newEmployee.id]: employeeName }));
    setAssignedEmployees(prev => [...prev, newEmployee.id]);
    setShowNewEmployeeForm(false);
    setNewEmployeeName('');
  }, [newEmployeeName, orgData.employees, addEmployee, divisionKey, dept?.number]);

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const toggleEmployee = (empId: string) => {
    if (assignedEmployees.includes(empId)) {
      setAssignedEmployees(assignedEmployees.filter(id => id !== empId));
    } else {
      setAssignedEmployees([...assignedEmployees, empId]);
    }
  };

  const addObjective = () => {
    setObjectives([...objectives, '']);
  };

  const updateObjective = (index: number, value: string) => {
    const newObjectives = [...objectives];
    newObjectives[index] = value;
    setObjectives(newObjectives);
  };

  const removeObjective = (index: number) => {
    setObjectives(objectives.filter((_, i) => i !== index));
  };

  if (!func) return null;

  return (
    <div className="post-inline-edit" onClick={handleContainerClick} ref={formRef} style={{ '--edit-color': divisionColor } as React.CSSProperties}>
      {/* Header */}
      <div className="inline-edit-header post-edit-header" style={{ backgroundColor: divisionColor }}>
        <div className="inline-header-left">
          <span className="inline-post-label">{t('edit.title')}</span>
        </div>
        <div className="inline-header-right">
          <button className="inline-close-btn" onClick={handleCancel} title={t('general.cancel')}>×</button>
        </div>
      </div>

      {/* Body */}
      <div className="inline-edit-body">
        {/* Name field */}
        <div className="inline-field-group">
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('edit.name')}
            className="inline-name-input"
            autoFocus
            onClick={e => e.stopPropagation()}
          />
        </div>

        {/* Assigned employees */}
        <div className="inline-field-group inline-assigned-group">
          <label className="inline-field-label">{t('edit.responsible')}</label>
          <div className="inline-assigned-badges">
            {assignedEmployees.length > 0 ? (
              assignedEmployees.map(empId => {
                const emp = getEmployeeById(empId);
                const empName = emp?.name || newEmployeeCache[empId];
                return empName ? (
                  <span key={empId} className="inline-assigned-badge">
                    {empName}
                    <button onClick={(e) => { e.stopPropagation(); toggleEmployee(empId); }} className="inline-badge-remove">×</button>
                  </span>
                ) : null;
              })
            ) : (
              <span className="inline-no-assignment">{t('edit.notAssigned')}</span>
            )}
          </div>

          {showNewEmployeeForm ? (
            <div className="inline-new-employee-form">
              <input
                type="text"
                value={newEmployeeName}
                onChange={e => setNewEmployeeName(e.target.value)}
                placeholder={t('edit.employeeName')}
                onClick={e => e.stopPropagation()}
                className="inline-new-manager-input"
              />
              <button onClick={handleCreateNewEmployee} disabled={!newEmployeeName.trim()} className="inline-confirm-btn">✓</button>
              <button onClick={(e) => { e.stopPropagation(); setShowNewEmployeeForm(false); setNewEmployeeName(''); }} className="inline-cancel-small">×</button>
            </div>
          ) : (
            <div className="inline-employee-picker">
              <select
                value=""
                onChange={e => { if (e.target.value) toggleEmployee(e.target.value); }}
                onClick={e => e.stopPropagation()}
                className="inline-manager-dropdown"
              >
                <option value="">{t('edit.selectExistingEmployee')}</option>
                {allEmployees
                  .filter(emp => !assignedEmployees.includes(emp.id))
                  .map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
              </select>
              <button onClick={(e) => { e.stopPropagation(); setShowNewEmployeeForm(true); }} className="inline-add-btn" title={t('edit.newEmployee')}>+</button>
            </div>
          )}
        </div>

        {/* Objectives */}
        <div className="inline-field-group inline-objectives-group">
          <label className="inline-field-label">{t('edit.mainActions')}</label>
          <div className="inline-objectives-list">
            {objectives.map((obj, idx) => (
              <div key={idx} className="inline-objective-row">
                <input
                  type="text"
                  value={obj}
                  onChange={e => updateObjective(idx, e.target.value)}
                  placeholder={t('edit.action')}
                  onClick={e => e.stopPropagation()}
                  className="inline-objective-input"
                />
                <button className="inline-objective-remove" onClick={(e) => { e.stopPropagation(); removeObjective(idx); }}>×</button>
              </div>
            ))}
            <button className="inline-add-objective-btn" onClick={(e) => { e.stopPropagation(); addObjective(); }}>
              {t('edit.addAction')}
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="inline-edit-footer">
        <button className="btn-inline-save" onClick={handleSave}>
          {t('general.save')}
        </button>
      </div>
    </div>
  );
};

export interface EditingState {
  type: EditType;
  divisionKey: DivisionKey;
  deptIndex?: number;
  sectionIndex?: number;  // NEW: for section operations
  funcIndex?: number;
  execId?: string;  // For executive function editing
  position: { top: number; left: number };
}

interface InlineEditPopoverProps {
  editing: EditingState | null;
  onClose: () => void;
}

export const InlineEditPopover: React.FC<InlineEditPopoverProps> = ({ editing, onClose }) => {
  const {
    orgData,
    updateDivision,
    updateDepartment,
    addDepartment,
    removeDepartment,
    addSection,
    updateSection,
    removeSection,
    updateFunction,
    addFunction,
    removeFunction,
    moveFunction,
    updateAssignments,
    updateExecutiveFunction,
    removeExecutiveFunction,
    getEmployeeById,
    addEmployee
  } = useOrgData();
  const { t } = useLocale();

  const popoverRef = useRef<HTMLDivElement>(null);
  // Track what editing session we initialized for (to avoid re-init on orgData changes)
  const lastInitializedEditingRef = useRef<string | null>(null);

  // Local state for editing
  const [name, setName] = useState('');
  const [vfp, setVfp] = useState('');
  const [managerId, setManagerId] = useState<string>('');
  const [color, setColor] = useState('');
  const [icon, setIcon] = useState('');
  const [objectives, setObjectives] = useState<string[]>([]);
  const [assignedEmployees, setAssignedEmployees] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [managesDivisions, setManagesDivisions] = useState<string[]>([]); // For executive functions

  // State for creating new employee
  const [showNewEmployeeForm, setShowNewEmployeeForm] = useState(false);
  const [newEmployeeName, setNewEmployeeName] = useState('');
  // Cache for newly created employees (to display immediately before context updates)
  const [newEmployeeCache, setNewEmployeeCache] = useState<Record<string, string>>({});

  // Get all employees for assignment dropdown (exclude only owner/board, allow executives as managers)
  const allEmployees = orgData.employees.filter(e =>
    e.id !== 'owner' && e.id !== '0' && e.id !== 'board'
  );

  // Generate a unique key for the current editing session
  const getEditingKey = (e: EditingState | null) => {
    if (!e) return null;
    return `${e.type}-${e.divisionKey}-${e.deptIndex}-${e.sectionIndex}-${e.funcIndex}-${e.execId}`;
  };

  // Load data when editing changes - only initialize once per editing session
  useEffect(() => {
    if (!editing) {
      lastInitializedEditingRef.current = null;
      return;
    }

    const editingKey = getEditingKey(editing);

    // Skip if we already initialized for this exact editing session
    if (lastInitializedEditingRef.current === editingKey) {
      return;
    }
    lastInitializedEditingRef.current = editingKey;

    // Reset new employee form
    setShowNewEmployeeForm(false);
    setNewEmployeeName('');
    setNewEmployeeCache({});

    // Reset state for new items
    if (editing.type === 'new-department' || editing.type === 'new-section' || editing.type === 'new-function') {
      setName('');
      setVfp('');
      setManagerId('');
      setColor('');
      setObjectives([]);
      setAssignedEmployees([]);
      setManagesDivisions([]);
      return;
    }

    // Handle executive function editing
    if (editing.type === 'executive' && editing.execId) {
      const execFunc = orgData.executiveFunctions?.find(f => f.id === editing.execId);
      if (execFunc) {
        setName(execFunc.name);
        setManagesDivisions(execFunc.managesDivisions || []);
        setAssignedEmployees(orgData.assignments?.[execFunc.id] || []);
        setObjectives(execFunc.objectives || []);
        setIcon(execFunc.icon || '');
        setColor(execFunc.color || '');
      }
      return;
    }

    const division = orgData.divisions[editing.divisionKey];
    if (!division) return;

    if (editing.type === 'division') {
      setName(division.name);
      setVfp(division.vfp || '');
      setManagerId(division.managerId || '');
      setColor(division.color || '');
      setIcon(division.icon || '');
    } else if (editing.type === 'department' && editing.deptIndex !== undefined) {
      const dept = division.departments?.[editing.deptIndex];
      if (dept) {
        setName(dept.name);
        setVfp(dept.vfp || '');
        setManagerId(dept.managerId || '');
      }
    } else if (editing.type === 'section' && editing.deptIndex !== undefined && editing.sectionIndex !== undefined) {
      // Load section data
      const dept = division.departments?.[editing.deptIndex];
      const section = dept?.sections?.[editing.sectionIndex];
      if (section) {
        setName(section.name);
        setManagerId(section.managerId || '');
      }
    } else if (editing.type === 'function' && editing.deptIndex !== undefined && editing.sectionIndex !== undefined && editing.funcIndex !== undefined) {
      const dept = division.departments?.[editing.deptIndex];
      const section = dept?.sections?.[editing.sectionIndex];
      const func = section?.functions?.[editing.funcIndex];
      const funcId = func?.id || `${editing.divisionKey}-d${editing.deptIndex}-s${editing.sectionIndex}-f${editing.funcIndex}`;

      if (func) {
        setName(func.name);
        setObjectives(func.objectives || []);
        setAssignedEmployees(orgData.assignments?.[funcId] || []);
      }
    }
  }, [editing, orgData]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleSave = () => {
    if (!editing) return;

    if (editing.type === 'executive' && editing.execId) {
      // Update executive function
      updateExecutiveFunction(editing.execId, {
        name,
        managesDivisions,
        objectives: objectives.filter(o => o.trim()),
        icon: icon || undefined,
        color: color || undefined
      });
      // Update employee assignment
      updateAssignments(editing.execId, assignedEmployees);
    } else if (editing.type === 'division') {
      updateDivision(editing.divisionKey, { name, vfp, managerId: managerId || undefined, color: color || undefined, icon: icon || undefined });
    } else if (editing.type === 'department' && editing.deptIndex !== undefined) {
      updateDepartment(editing.divisionKey, editing.deptIndex, { name, vfp, managerId: managerId || undefined });
    } else if (editing.type === 'section' && editing.deptIndex !== undefined && editing.sectionIndex !== undefined) {
      // Update existing section
      updateSection(editing.divisionKey, editing.deptIndex, editing.sectionIndex, {
        name,
        managerId: managerId || undefined
      });
    } else if (editing.type === 'function' && editing.deptIndex !== undefined && editing.sectionIndex !== undefined && editing.funcIndex !== undefined) {
      const division = orgData.divisions[editing.divisionKey];
      const dept = division?.departments?.[editing.deptIndex];
      const section = dept?.sections?.[editing.sectionIndex];
      const func = section?.functions?.[editing.funcIndex];
      const funcId = func?.id || `${editing.divisionKey}-d${editing.deptIndex}-s${editing.sectionIndex}-f${editing.funcIndex}`;

      updateFunction(editing.divisionKey, editing.deptIndex, editing.sectionIndex, editing.funcIndex, {
        name,
        objectives
      });
      updateAssignments(funcId, assignedEmployees);
    } else if (editing.type === 'new-department') {
      if (name.trim()) {
        addDepartment(editing.divisionKey, {
          name: name.trim(),
          vfp: vfp.trim(),
          managerId: managerId || undefined
        });
      }
    } else if (editing.type === 'new-section' && editing.deptIndex !== undefined) {
      // Create new section
      if (name.trim()) {
        addSection(editing.divisionKey, editing.deptIndex, {
          name: name.trim(),
          managerId: managerId || undefined
        });
      }
    } else if (editing.type === 'new-function' && editing.deptIndex !== undefined && editing.sectionIndex !== undefined) {
      if (name.trim()) {
        const newFunc: OrgFunction = {
          id: `${editing.divisionKey}-d${editing.deptIndex}-s${editing.sectionIndex}-f${Date.now()}`,
          name: name.trim(),
          objectives: objectives.filter(o => o.trim())
        };
        addFunction(editing.divisionKey, editing.deptIndex, editing.sectionIndex, newFunc);
      }
    }

    onClose();
  };

  const handleDelete = () => {
    if (!editing) return;

    if (editing.type === 'executive' && editing.execId) {
      removeExecutiveFunction(editing.execId);
    } else if (editing.type === 'department' && editing.deptIndex !== undefined) {
      removeDepartment(editing.divisionKey, editing.deptIndex);
    } else if (editing.type === 'section' && editing.deptIndex !== undefined && editing.sectionIndex !== undefined) {
      removeSection(editing.divisionKey, editing.deptIndex, editing.sectionIndex);
    } else if (editing.type === 'function' && editing.deptIndex !== undefined && editing.sectionIndex !== undefined && editing.funcIndex !== undefined) {
      removeFunction(editing.divisionKey, editing.deptIndex, editing.sectionIndex, editing.funcIndex);
    }

    onClose();
  };

  const addObjective = () => {
    setObjectives([...objectives, '']);
  };

  const updateObjective = (index: number, value: string) => {
    const newObjectives = [...objectives];
    newObjectives[index] = value;
    setObjectives(newObjectives);
  };

  const removeObjective = (index: number) => {
    setObjectives(objectives.filter((_, i) => i !== index));
  };

  const moveObjectiveUp = (index: number) => {
    if (index === 0) return;
    const newObjectives = [...objectives];
    [newObjectives[index - 1], newObjectives[index]] = [newObjectives[index], newObjectives[index - 1]];
    setObjectives(newObjectives);
  };

  const moveObjectiveDown = (index: number) => {
    if (index === objectives.length - 1) return;
    const newObjectives = [...objectives];
    [newObjectives[index], newObjectives[index + 1]] = [newObjectives[index + 1], newObjectives[index]];
    setObjectives(newObjectives);
  };

  const toggleEmployee = (empId: string) => {
    if (assignedEmployees.includes(empId)) {
      setAssignedEmployees(assignedEmployees.filter(id => id !== empId));
    } else {
      setAssignedEmployees([...assignedEmployees, empId]);
    }
  };

  const handleCreateNewEmployee = () => {
    if (!editing || !newEmployeeName.trim()) return;

    const division = orgData.divisions[editing.divisionKey];
    if (!division) return;

    // Find the CEO as default parent (roles come from assignments, not title)
    const ceo = orgData.employees.find(e => e.id === 'ceo' || e.id === '1');
    const parentId = ceo?.id || '1';

    // Get department number if we're in a department context
    const dept = editing.deptIndex !== undefined ? division.departments?.[editing.deptIndex] : undefined;

    const employeeName = newEmployeeName.trim();
    const newEmployee = addEmployee({
      name: employeeName,
      division: editing.divisionKey,
      deptNum: dept?.number,
      parentId
    });

    // Cache the new employee for immediate display
    setNewEmployeeCache(prev => ({ ...prev, [newEmployee.id]: employeeName }));
    // Add the new employee to the assigned list
    setAssignedEmployees(prev => [...prev, newEmployee.id]);

    // Reset the form
    setShowNewEmployeeForm(false);
    setNewEmployeeName('');
  };

  if (!editing) return null;

  // For executive editing, we don't need a division
  const division = editing.type !== 'executive' ? orgData.divisions[editing.divisionKey] : null;
  if (editing.type !== 'executive' && !division) return null;

  const getTitle = () => {
    switch (editing.type) {
      case 'executive':
        const execFunc = orgData.executiveFunctions?.find(f => f.id === editing.execId);
        return t('edit.editExecFunction', { name: execFunc?.name || t('edit.execFunction') });
      case 'division':
        return t('edit.editDivision', { number: division?.number || '' });
      case 'department':
        const dept = division?.departments?.[editing.deptIndex!];
        return t('edit.editUnit', { number: dept?.number || '' });
      case 'section':
        return t('edit.editSection');
      case 'function':
        return t('edit.title');
      case 'new-department':
        return t('edit.newUnit');
      case 'new-section':
        return t('edit.newSection');
      case 'new-function':
        return t('edit.new');
      default:
        return t('edit.title');
    }
  };

  // Allow delete for C-level (not CEO/Owner), departments, sections, functions
  const isCeoOrOwner = editing.type === 'executive' && (editing.execId === 'exec-ceo' || editing.execId === 'exec-owner');
  const canDelete = (editing.type === 'executive' && !isCeoOrOwner) || editing.type === 'department' || editing.type === 'section' || editing.type === 'function';
  const isNew = editing.type === 'new-department' || editing.type === 'new-section' || editing.type === 'new-function';

  // Delete confirmation dialog
  if (showDeleteConfirm) {
    const itemType = editing.type === 'executive' ? t('edit.typeRole') : editing.type === 'department' ? t('edit.typeUnit') : editing.type === 'section' ? t('edit.typeSection') : t('edit.typeRole');
    return (
      <div
        ref={popoverRef}
        className="inline-edit-popover"
        style={{
          top: editing.position.top,
          left: editing.position.left
        }}
      >
        <div className="popover-header delete-confirm-header">
          <span className="popover-title">{t('edit.confirmDelete')}</span>
        </div>
        <div className="popover-content delete-confirm-content">
          <p className="delete-warning">
            {t('edit.deleteWarning', { type: itemType, name })}
          </p>
          <p className="delete-warning-sub">{t('edit.cannotUndo')}</p>
        </div>
        <div className="popover-footer">
          <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>{t('general.cancel')}</button>
          <button className="btn-delete-confirm" onClick={handleDelete}>{t('general.delete')}</button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={popoverRef}
      className={`inline-edit-popover ${editing.type === 'executive' ? 'executive-popover' : ''}`}
      style={{
        top: editing.position.top,
        left: editing.position.left
      }}
    >
      <div className="popover-header" style={{ backgroundColor: editing.type === 'executive' ? '#87CEEB' : division?.color }}>
        <span className="popover-title">{getTitle()}</span>
        <button className="popover-close" onClick={onClose}>×</button>
      </div>

      <div className="popover-content">
        {/* Name field - always shown */}
        <div className="popover-field">
          <label>{t('edit.name')}</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('edit.name')}
            autoFocus
          />
        </div>

        {/* VFP field - for division, department, and new-department */}
        {(editing.type === 'division' || editing.type === 'department' || editing.type === 'new-department') && (
          <div className="popover-field">
            <label>{t('edit.vfp')}</label>
            <textarea
              value={vfp}
              onChange={e => setVfp(e.target.value)}
              placeholder={t('edit.finalProduct')}
              rows={2}
            />
          </div>
        )}

        {/* Executive function fields */}
        {editing.type === 'executive' && (
          <>
            {/* Assigned Employee */}
            <div className="popover-field">
              <label>{t('edit.assignedTo')}</label>
              <div className="assigned-employees">
                {assignedEmployees.length > 0 ? (
                  assignedEmployees.map(empId => {
                    const emp = getEmployeeById(empId);
                    const empName = emp?.name || newEmployeeCache[empId];
                    return empName ? (
                      <span key={empId} className="assigned-badge">
                        {empName}
                        <button onClick={() => setAssignedEmployees(assignedEmployees.filter(id => id !== empId))}>×</button>
                      </span>
                    ) : null;
                  })
                ) : (
                  <span className="no-assignment">{t('edit.notAssigned')}</span>
                )}
              </div>

              {/* New Employee Form for Executive */}
              {showNewEmployeeForm ? (
                <div className="new-employee-form">
                  <input
                    type="text"
                    value={newEmployeeName}
                    onChange={e => setNewEmployeeName(e.target.value)}
                    placeholder={t('edit.employeeName')}
                    autoFocus
                  />
                  <div className="new-employee-actions">
                    <button
                      className="btn-create-employee"
                      onClick={() => {
                        if (!newEmployeeName.trim()) return;
                        const ceo = orgData.employees.find(e => e.id === 'ceo' || e.id === '1');
                        const parentId = ceo?.id || '1';
                        const employeeName = newEmployeeName.trim();
                        const newEmployee = addEmployee({
                          name: employeeName,
                          division: 'executive' as DivisionKey,
                          parentId
                        });
                        // Cache the new employee for immediate display
                        setNewEmployeeCache(prev => ({ ...prev, [newEmployee.id]: employeeName }));
                        setAssignedEmployees(prev => [...prev, newEmployee.id]);
                        setShowNewEmployeeForm(false);
                        setNewEmployeeName('');
                      }}
                      disabled={!newEmployeeName.trim()}
                    >
                      {t('edit.createAndAdd')}
                    </button>
                    <button
                      className="btn-cancel-new"
                      onClick={() => {
                        setShowNewEmployeeForm(false);
                        setNewEmployeeName('');
                      }}
                    >
                      {t('general.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="employee-select-row">
                  <select
                    value=""
                    onChange={e => {
                      if (e.target.value) {
                        setAssignedEmployees([...assignedEmployees, e.target.value]);
                      }
                    }}
                  >
                    <option value="">{t('edit.selectEmployee')}</option>
                    {allEmployees
                      .filter(emp => !assignedEmployees.includes(emp.id))
                      .map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                  </select>
                  <button
                    className="btn-new-employee"
                    onClick={() => setShowNewEmployeeForm(true)}
                    title={t('edit.createNewEmployee')}
                  >
                    {t('edit.newEmployee')}
                  </button>
                </div>
              )}
            </div>

            {/* Managed Divisions (for C-level, not CEO/Owner) */}
            {editing.execId !== 'exec-ceo' && editing.execId !== 'exec-owner' && (
              <div className="popover-field">
                <label>{t('edit.managedDivisions')}</label>
                <div className="divisions-checkbox-list">
                  {Object.entries(orgData.divisions)
                    .sort((a, b) => a[1].number - b[1].number)
                    .map(([divKey, div]) => (
                      <label key={divKey} className="division-checkbox">
                        <input
                          type="checkbox"
                          checked={managesDivisions.includes(divKey)}
                          onChange={e => {
                            if (e.target.checked) {
                              setManagesDivisions([...managesDivisions, divKey]);
                            } else {
                              setManagesDivisions(managesDivisions.filter(d => d !== divKey));
                            }
                          }}
                        />
                        <span className="division-checkbox-label">
                          {div.icon} {div.number}. {div.name}
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            )}

            {/* Icon picker for executive */}
            <div className="popover-field">
              <label>{t('edit.icon')}</label>
              <div className="icon-picker-row">
                <span className="icon-preview">{icon || '👔'}</span>
                <input
                  type="text"
                  value={icon}
                  onChange={e => setIcon(e.target.value)}
                  placeholder="👔"
                  className="icon-input"
                  maxLength={2}
                />
              </div>
            </div>

            {/* Color picker for executive */}
            <div className="popover-field">
              <label>{t('edit.color')}</label>
              <div className="color-picker-row">
                <input
                  type="color"
                  value={color || '#87CEEB'}
                  onChange={e => setColor(e.target.value)}
                  className="color-picker-input"
                />
                <input
                  type="text"
                  value={color}
                  onChange={e => setColor(e.target.value)}
                  placeholder="#87CEEB"
                  className="color-hex-input"
                />
              </div>
            </div>
          </>
        )}

        {/* Manager field - for division, department, section, new-department, and new-section */}
        {(editing.type === 'division' || editing.type === 'department' || editing.type === 'new-department' || editing.type === 'section' || editing.type === 'new-section') && (
          <div className="popover-field">
            <label>{t('edit.manager')}</label>
            <div className="assigned-employees">
              {managerId ? (
                <span className="assigned-badge manager-badge">
                  {getEmployeeById(managerId)?.name || newEmployeeCache[managerId] || t('edit.notFound')}
                  <button onClick={() => setManagerId('')}>×</button>
                </span>
              ) : (
                <span className="no-assignment">{t('edit.notAssigned')}</span>
              )}
            </div>

            {/* New Employee Form for Manager */}
            {showNewEmployeeForm ? (
              <div className="new-employee-form">
                <input
                  type="text"
                  value={newEmployeeName}
                  onChange={e => setNewEmployeeName(e.target.value)}
                  placeholder={t('edit.managerName')}
                  autoFocus
                />
                <div className="new-employee-actions">
                  <button
                    className="btn-create-employee"
                    onClick={() => {
                      if (!editing || !newEmployeeName.trim()) return;
                      const division = orgData.divisions[editing.divisionKey];
                      if (!division) return;
                      const ceo = orgData.employees.find(e => e.id === 'ceo' || e.id === '1');
                      const parentId = ceo?.id || '1';
                      const dept = editing.deptIndex !== undefined ? division.departments?.[editing.deptIndex] : undefined;
                      const employeeName = newEmployeeName.trim();
                      const newEmployee = addEmployee({
                        name: employeeName,
                        division: editing.divisionKey,
                        deptNum: dept?.number,
                        parentId
                      });
                      // Cache the new employee for immediate display
                      setNewEmployeeCache(prev => ({ ...prev, [newEmployee.id]: employeeName }));
                      setManagerId(newEmployee.id);
                      setShowNewEmployeeForm(false);
                      setNewEmployeeName('');
                    }}
                    disabled={!newEmployeeName.trim()}
                  >
                    {t('edit.createAsManager')}
                  </button>
                  <button
                    className="btn-cancel-new"
                    onClick={() => {
                      setShowNewEmployeeForm(false);
                      setNewEmployeeName('');
                    }}
                  >
                    {t('general.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="employee-select-row">
                <select
                  value=""
                  onChange={e => {
                    if (e.target.value) {
                      setManagerId(e.target.value);
                    }
                  }}
                >
                  <option value="">{t('edit.selectManager')}</option>
                  {allEmployees
                    .filter(emp => emp.id !== managerId)
                    .map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                </select>
                <button
                  className="btn-new-employee"
                  onClick={() => setShowNewEmployeeForm(true)}
                  title={t('edit.createNewManager')}
                >
                  {t('edit.newEmployee')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Employee assignment - for function and new-function */}
        {(editing.type === 'function' || editing.type === 'new-function') && (
          <>
            <div className="popover-field">
              <label>{t('edit.responsible')}</label>
              <div className="assigned-employees">
                {assignedEmployees.length > 0 ? (
                  assignedEmployees.map(empId => {
                    const emp = getEmployeeById(empId);
                    const empName = emp?.name || newEmployeeCache[empId];
                    return empName ? (
                      <span key={empId} className="assigned-badge">
                        {empName}
                        <button onClick={() => toggleEmployee(empId)}>×</button>
                      </span>
                    ) : null;
                  })
                ) : (
                  <span className="no-assignment">{t('edit.notAssigned')}</span>
                )}
              </div>

              {/* New Employee Form */}
              {showNewEmployeeForm ? (
                <div className="new-employee-form">
                  <input
                    type="text"
                    value={newEmployeeName}
                    onChange={e => setNewEmployeeName(e.target.value)}
                    placeholder={t('edit.employeeName')}
                    autoFocus
                  />
                  <div className="new-employee-actions">
                    <button
                      className="btn-create-employee"
                      onClick={handleCreateNewEmployee}
                      disabled={!newEmployeeName.trim()}
                    >
                      {t('edit.createAndAdd')}
                    </button>
                    <button
                      className="btn-cancel-new"
                      onClick={() => {
                        setShowNewEmployeeForm(false);
                        setNewEmployeeName('');
                      }}
                    >
                      {t('general.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="employee-select-row">
                  <select
                    value=""
                    onChange={e => {
                      if (e.target.value) {
                        toggleEmployee(e.target.value);
                      }
                    }}
                  >
                    <option value="">{t('edit.selectExistingEmployee')}</option>
                    {allEmployees
                      .filter(emp => !assignedEmployees.includes(emp.id))
                      .map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                  </select>
                  <button
                    className="btn-new-employee"
                    onClick={() => setShowNewEmployeeForm(true)}
                    title={t('edit.createNewEmployee')}
                  >
                    {t('edit.newEmployee')}
                  </button>
                </div>
              )}
            </div>

            {/* Objectives */}
            <div className="popover-field">
              <label>{t('edit.mainActions')}</label>
              <div className="objectives-list">
                {objectives.map((obj, idx) => (
                  <div key={idx} className="objective-row">
                    <div className="reorder-btns">
                      <button
                        className="move-btn"
                        onClick={() => moveObjectiveUp(idx)}
                        disabled={idx === 0}
                        title={t('edit.moveUp')}
                      >▲</button>
                      <button
                        className="move-btn"
                        onClick={() => moveObjectiveDown(idx)}
                        disabled={idx === objectives.length - 1}
                        title={t('edit.moveDown')}
                      >▼</button>
                    </div>
                    <input
                      type="text"
                      value={obj}
                      onChange={e => updateObjective(idx, e.target.value)}
                      placeholder={t('edit.action')}
                    />
                    <button className="remove-btn" onClick={() => removeObjective(idx)}>×</button>
                  </div>
                ))}
                <button className="add-objective-btn" onClick={addObjective}>
                  {t('edit.addAction')}
                </button>
              </div>
            </div>

            {/* Move to different department - only for existing functions */}
            {editing.type === 'function' && editing.sectionIndex !== undefined && (
              <div className="popover-field">
                <label>{t('edit.moveToSection')}</label>
                <select
                  value=""
                  onChange={e => {
                    if (!e.target.value || !editing) return;
                    const [toDivKey, toDeptIdx, toSectionIdx] = e.target.value.split(':');
                    moveFunction(
                      editing.divisionKey,
                      editing.deptIndex!,
                      editing.sectionIndex!,
                      editing.funcIndex!,
                      toDivKey as DivisionKey,
                      parseInt(toDeptIdx),
                      parseInt(toSectionIdx)
                    );
                    onClose();
                  }}
                >
                  <option value="">{t('edit.selectSection')}</option>
                  {Object.entries(orgData.divisions)
                    .sort((a, b) => a[1].number - b[1].number)
                    .map(([divKey, div]) => (
                      <optgroup key={divKey} label={`${div.number}. ${div.name}`}>
                        {div.departments?.map((dept, deptIdx) =>
                          dept.sections?.map((section, sectionIdx) => {
                            // Skip current section
                            if (divKey === editing.divisionKey && deptIdx === editing.deptIndex && sectionIdx === editing.sectionIndex) return null;
                            return (
                              <option key={`${divKey}:${deptIdx}:${sectionIdx}`} value={`${divKey}:${deptIdx}:${sectionIdx}`}>
                                {t('edit.unit')} {dept.number} - {section.name}
                              </option>
                            );
                          })
                        )}
                      </optgroup>
                    ))}
                </select>
              </div>
            )}
          </>
        )}

        {/* Icon/Emoji picker - for division only (at end of form) */}
        {editing.type === 'division' && (
          <div className="popover-field">
            <label>{t('edit.icon')}</label>
            <div className="icon-picker-row">
              <span className="icon-preview">{icon || '📁'}</span>
              <input
                type="text"
                value={icon}
                onChange={e => setIcon(e.target.value)}
                placeholder="📁"
                className="icon-input"
                maxLength={2}
              />
            </div>
          </div>
        )}

        {/* Color picker - for division only (at end of form) */}
        {editing.type === 'division' && (
          <div className="popover-field">
            <label>{t('edit.color')}</label>
            <div className="color-picker-row">
              <input
                type="color"
                value={color || '#667eea'}
                onChange={e => setColor(e.target.value)}
                className="color-picker-input"
              />
              <input
                type="text"
                value={color}
                onChange={e => setColor(e.target.value)}
                placeholder="#667eea"
                className="color-hex-input"
              />
            </div>
          </div>
        )}
      </div>

      <div className="popover-footer">
        {canDelete && (
          <button className="btn-delete" onClick={() => setShowDeleteConfirm(true)}>
            {t('general.delete')}
          </button>
        )}
        <div className="footer-spacer" />
        <button className="btn-cancel" onClick={onClose}>{t('general.cancel')}</button>
        <button className="btn-save" onClick={handleSave}>
          {isNew ? t('edit.create') : t('general.save')}
        </button>
      </div>
    </div>
  );
};
